import React, { useState, useEffect } from "react";
import "./styles/adult.css"; // reuse adult styling

// Robust DOB parser (same as other forms)
function parseDOBtoDate(dobStr) {
  if (!dobStr) return null;
  const s = String(dobStr).trim();
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const [, mm, dd, yyyy] = m;
    const dt = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (!Number.isNaN(dt.getTime())) return dt;
  }
}
function calculateAge(dobStr) {
  if (!dobStr) return null;
  const dobDate = parseDOBtoDate(dobStr);
  if (!dobDate) return null;
  const now = new Date();
  let age = now.getFullYear() - dobDate.getFullYear();
  const m = now.getMonth() - dobDate.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dobDate.getDate())) age--;
  return Number.isFinite(age) ? age : null;
}

export default function ChildrenForm({ age: propAge = null, onComplete }) {
  // Basic fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  // Honeypot (should remain empty)
  const [website, setWebsite] = useState("");

  // On-grounds vs traveling
  // "with-parent-guardian" | "traveling"
  const [groundsOption, setGroundsOption] = useState("");
  const [parentGuardianNames, setParentGuardianNames] = useState("");
  const [travelingWithName, setTravelingWithName] = useState("");
  const [travelingWithPhone, setTravelingWithPhone] = useState("");

  // Program fields
  const [attendingNursery, setAttendingNursery] = useState("no"); // ages 2-4
  const [attendingVBS, setAttendingVBS] = useState("no"); // ages 5-10

  // Accessibility & emergency
  const [emergency1Name, setEmergency1Name] = useState("");
  const [emergency1Phone, setEmergency1Phone] = useState("");
  const [emergency2Name, setEmergency2Name] = useState("");
  const [emergency2Phone, setEmergency2Phone] = useState("");

  // Allergies
  const [hasAllergies, setHasAllergies] = useState("no");
  const [allergies, setAllergies] = useState("");
  const [allergyAcknowledged, setAllergyAcknowledged] = useState(false);

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function formatDobMMDDYYYY(input) {
    const digits = String(input || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  }

  function handleDobChange(e) {
    setDob(formatDobMMDDYYYY(e.target.value));
  }

  // derivedAge: prefer propAge (passed from MultiStepForm) else compute from local dob
  const derivedAge = propAge !== null ? Number(propAge) : dob ? calculateAge(dob) : null;

  // Debug: log the ages so you can see what's actually being used in the browser console
  useEffect(() => {
    console.log("ChildrenForm: propAge:", propAge, "dob:", dob, "derivedAge:", derivedAge);
  }, [propAge, dob, derivedAge]);

  const showNurseryQuestion = derivedAge !== null && derivedAge >= 2 && derivedAge <= 4;
  const showVBSQuestion = derivedAge !== null && derivedAge >= 5 && derivedAge <= 10;
  const isMiddleSchool = derivedAge !== null && derivedAge >= 11 && derivedAge <= 12;
  const noProgramQuestion = derivedAge !== null && derivedAge <= 1;

  const validate = () => {
    if (!firstName.trim()) return "First name is required.";
    if (!lastName.trim()) return "Last name is required.";
    if (!dob) return "Date of birth is required.";
    const a = calculateAge(dob);
    if (a === null && propAge === null) return "Please enter a valid date of birth.";
    const numericAge = propAge !== null ? Number(propAge) : a;
    if (numericAge < 0 || numericAge > 12) return "This children form is for ages 0–12.";
    if (!phone.trim()) return "Phone is required.";
    if (!email.trim()) return "Email is required.";

    if (!groundsOption) {
      return "Please select whether the child is staying with a parent/guardian or traveling to the grounds.";
    }
    if (groundsOption === "with-parent-guardian" && !parentGuardianNames.trim()) {
      return "Please enter the name(s) of the parent/guardian staying on the grounds with the child.";
    }
    if (groundsOption === "traveling") {
      if (!travelingWithName.trim()) return "Please enter who will be bringing the child.";
      if (!travelingWithPhone.trim()) return "Please enter a phone number for the person bringing the child.";
    }

    if (showNurseryQuestion && (attendingNursery !== "yes" && attendingNursery !== "no")) {
      return "Please indicate nursery attendance.";
    }
    if (showVBSQuestion && (attendingVBS !== "yes" && attendingVBS !== "no")) {
      return "Please indicate VBS attendance.";
    }

    if (!emergency1Name.trim()) return "Emergency contact 1 name is required.";
    if (!emergency1Phone.trim()) return "Emergency contact 1 phone is required.";

    if (hasAllergies === "yes") {
      if (!allergies.trim()) return "Please list allergies.";
      if (!allergyAcknowledged) return "Please acknowledge the allergy policy.";
    }
    return null;
  };

  const buildPayload = () => {
    const payload = {
      formType: "children",
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob: dob || undefined,
      age: derivedAge !== null ? String(derivedAge) : undefined,
      phone: phone.trim(),
      email: email.trim(),

      // honeypot (keep in payload so the server can also reject bots)
      website: website.trim() || undefined,

      groundsOption: groundsOption || undefined,
      parentGuardianNames:
        groundsOption === "with-parent-guardian" ? parentGuardianNames.trim() : undefined,
      travelingWithName: groundsOption === "traveling" ? travelingWithName.trim() : undefined,
      travelingWithPhone: groundsOption === "traveling" ? travelingWithPhone.trim() : undefined,

      emergency1Name: emergency1Name.trim() || undefined,
      emergency1Phone: emergency1Phone.trim() || undefined,
      emergency2Name: emergency2Name.trim() || undefined,
      emergency2Phone: emergency2Phone.trim() || undefined,

      hasAllergies,
      allergies: hasAllergies === "yes" ? allergies.trim() : undefined,
      allergyAcknowledged: !!allergyAcknowledged,
    };

    if (showNurseryQuestion) payload.attendingNursery = attendingNursery;
    if (showVBSQuestion) payload.attendingVBS = attendingVBS;
    if (isMiddleSchool) payload.program = "middle-school";

    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Honeypot: bots fill this, humans won't
    if (String(website || "").trim()) {
      // Fail silently to avoid giving bots feedback
      return;
    }

    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    const payload = buildPayload();
    setSubmitting(true);

    if (typeof onComplete === "function") {
      onComplete(payload);
      return;
    }

    // fallback standalone submit
    fetch("/.netlify/functions/append-sheet", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Submission failed");
        window.location.href = "/policy";
      })
      .catch((err) => {
        setError(err.message || "Submit failed");
        setSubmitting(false);
      });
  };

  return (
    <form className="adult-form" onSubmit={handleSubmit} noValidate>
      {/* Honeypot: hidden field to catch bots (should stay empty) */}
      <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Website
          <input
            type="text"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
          />
        </label>
      </div>

      <h2>Child Registration (0–12)</h2>
      <p className="muted small">Fill out the child's information. Required fields are marked *</p>

      <div className="two-col">
        <div className="field">
          <label>
            First Name <span className="required">*</span>
          </label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required />
        </div>
        <div className="field">
          <label>
            Last Name <span className="required">*</span>
          </label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required />
        </div>
      </div>

      <div className="two-col">
        <div className="field">
          <label>
            Date of Birth <span className="required">*</span>
          </label>
          <input
            type="text"
            value={dob}
            onChange={handleDobChange}
            required placeholder="MM/DD/YYYY"
            inputMode="numeric"
            maxLength={10}
          />
        </div>
        <div className="field">
          <label>
            Parent / Guardian Phone <span className="required">*</span>
          </label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      <div className="field">
        <label>
          Parent / Guardian Email <span className="required">*</span>
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
        />
      </div>

      {/* Program question logic */}
      {noProgramQuestion && (
        <div className="field">
          <p className="muted">No nursery or VBS question for infants (0–1).</p>
        </div>
      )}

      {showNurseryQuestion && (
        <div className="field">
          <label>
            Will the child be attending nursery? <span className="required">*</span>
          </label>
          <select value={attendingNursery} onChange={(e) => setAttendingNursery(e.target.value)} required>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      )}

      {showVBSQuestion && (
        <div className="field">
          <label>
            Will the child be in VBS? <span className="required">*</span>
          </label>
          <select value={attendingVBS} onChange={(e) => setAttendingVBS(e.target.value)} required>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
          <small className="muted">VBS is for ages 5–10.</small>
        </div>
      )}

      {isMiddleSchool && (
        <div className="field">
          <p className="muted">This child will be placed in the middle school class (ages 11–12).</p>
        </div>
      )}

      {/* On-grounds vs traveling */}
      <div className="field">
        <label className="field-label">
          Will the child be staying on the grounds with a parent/guardian or traveling to the grounds?
          <span className="required"> *</span>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <input
            type="radio"
            name="groundsOption"
            checked={groundsOption === "with-parent-guardian"}
            onChange={() => {
              setGroundsOption("with-parent-guardian");
              // clear opposite-path fields
              setTravelingWithName("");
              setTravelingWithPhone("");
            }}
            required
          />
          Staying on the grounds with a parent/guardian
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <input
            type="radio"
            name="groundsOption"
            checked={groundsOption === "traveling"}
            onChange={() => {
              setGroundsOption("traveling");
              // clear opposite-path fields
              setParentGuardianNames("");
            }}
            required
          />
          Traveling to the grounds
        </label>
      </div>

      {groundsOption === "with-parent-guardian" && (
        <div className="field">
          <label>
            Parent/Guardian name(s) staying on the grounds <span className="required">*</span>
          </label>
          <input
            value={parentGuardianNames}
            onChange={(e) => setParentGuardianNames(e.target.value)}
            required
            placeholder="e.g. Jane Doe, John Doe"
          />
        </div>
      )}

      {groundsOption === "traveling" && (
        <div className="two-col">
          <div className="field">
            <label>
              Who will be bringing the child? <span className="required">*</span>
            </label>
            <input
              value={travelingWithName}
              onChange={(e) => setTravelingWithName(e.target.value)}
              required
              placeholder="Name"
            />
          </div>

          <div className="field">
            <label>
              Phone number for that person <span className="required">*</span>
            </label>
            <input
              value={travelingWithPhone}
              onChange={(e) => setTravelingWithPhone(e.target.value)}
              required
              placeholder="e.g. 555-555-5555"
            />
          </div>
        </div>
      )}

      <h3>Emergency Contact 1</h3>
      <div className="two-col">
        <div className="field">
          <label>
            Name <span className="required">*</span>
          </label>
          <input value={emergency1Name}
            onChange={(e) => setEmergency1Name(e.target.value)}
            required
            placeholder="First & Last Name"
          />
        </div>
        <div className="field">
          <label>
            Phone <span className="required">*</span>
          </label>
          <input value={emergency1Phone}
            onChange={(e) => setEmergency1Phone(e.target.value)}
            required
            placeholder="e.g. 555-555-5555"
          />
        </div>
      </div>

      <h3>Emergency Contact 2</h3>
      <div className="two-col">
        <div className="field">
          <label>Name</label>
          <input
            value={emergency2Name}
            onChange={(e) => setEmergency2Name(e.target.value)}
            placeholder="First & Last Name (Optional)"
          />
        </div>
        <div className="field">
          <label>Phone</label>
          <input
            value={emergency2Phone}
            onChange={(e) => setEmergency2Phone(e.target.value)}
            placeholder="e.g. 555-555-5555 (Optional)"
          />
        </div>
      </div>

      <div className="field">
        <label>
          Does the child have allergies? <span className="required">*</span>
        </label>
        <select value={hasAllergies} onChange={(e) => setHasAllergies(e.target.value)} required>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </div>

      {hasAllergies === "yes" && (
        <div className="field allergy-block">
          <label>
            What are the child's allergies? <span className="required">*</span>
          </label>
          <input
            value={allergies}
            onChange={(e) => setAllergies(e.target.value)}
            required
            placeholder="e.g. Peanuts, Penicillin"
          />
          <div className="rooming-note" style={{ marginTop: 8 }}>
            <p className="muted" style={{ margin: 0 }}>
              Please note: we cannot guarantee there will not be cross contamination of food allergens.
            </p>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                checked={allergyAcknowledged}
                onChange={(e) => setAllergyAcknowledged(e.target.checked)}
              />{" "}
              I acknowledge the allergy policy above.
            </label>
          </div>
        </div>
      )}

      {error && (
        <div className="note" style={{ color: "crimson" }}>
          {error}
        </div>
      )}

      <div className="form-footer">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Submitting…" : "Next"}
        </button>
      </div>
    </form>
  );
}